import { useRef, type ReactNode } from 'react';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional title rendered in the dark header bar. */
  title?: ReactNode;
  /** Footer slot (typically Cancel + primary action Buttons). */
  footer?: ReactNode;
  children?: ReactNode;
  /** Max width of the centered card. Defaults to 640px. */
  maxWidth?: number | string;
  /** Pass false to disable backdrop-click closing. */
  closeOnBackdropClick?: boolean;
}

const css = `
  .ew-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: var(--ew-s-6) var(--ew-s-4);
    overflow-y: auto;
  }
  .ew-modal-card {
    background: #fff;
    border-radius: var(--ew-r-lg);
    width: 100%;
    box-shadow: var(--ew-shadow-3);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - var(--ew-s-6) * 2);
  }
  .ew-modal-header {
    padding: var(--ew-s-4) var(--ew-s-5);
    background: var(--ew-dark);
    color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .ew-modal-title {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1 var(--ew-font);
    margin: 0;
  }
  .ew-modal-close {
    background: transparent;
    border: 0;
    color: #fff;
    font-size: 22px;
    cursor: pointer;
    padding: 0 6px;
    line-height: 1;
  }
  .ew-modal-body {
    padding: var(--ew-s-5);
    overflow-y: auto;
  }
  .ew-modal-footer {
    padding: var(--ew-s-4) var(--ew-s-5);
    border-top: 1px solid var(--ew-n-200);
    display: flex;
    justify-content: flex-end;
    gap: var(--ew-s-2);
  }
`;

/**
 * Centered modal with backdrop and Escape-to-close, built on the shared
 * `useFocusTrap` hook so keyboard users can't tab into the background.
 *
 * @example
 *   <Modal
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     title="Start Post Project Review"
 *     footer={
 *       <>
 *         <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
 *         <Button onClick={save}>Start PPR</Button>
 *       </>
 *     }
 *   >
 *     <YourForm />
 *   </Modal>
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  footer,
  children,
  maxWidth = 640,
  closeOnBackdropClick = true,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(cardRef, { onEscape: isOpen ? onClose : undefined });

  if (!isOpen) return null;

  return (
    <>
      <style>{css}</style>
      <div
        className="ew-modal-backdrop"
        onClick={() => {
          if (closeOnBackdropClick) onClose();
        }}
      >
        <div
          ref={cardRef}
          className="ew-modal-card"
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : undefined}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth }}
        >
          {title !== undefined && (
            <div className="ew-modal-header">
              <h2 className="ew-modal-title">{title}</h2>
              <button
                type="button"
                className="ew-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}
          <div className="ew-modal-body">{children}</div>
          {footer && <div className="ew-modal-footer">{footer}</div>}
        </div>
      </div>
    </>
  );
}
